import { Bell, Building2, Code2, DatabaseZap, Gauge, Radio, SlidersHorizontal, Sparkles, UserRound, Users, type LucideIcon } from "lucide-react";

/**
 * Settings map (ALP-84 P4): two labelled groups, "You" (personal, every role edits) and "Organization" (owners and admins
 * edit, everyone else reads). One registry drives the side nav, the settings index (the list page on phones), and the
 * palette / feature map, so a page can never be reachable from one place and missing from another.
 */
export type SettingsGroup = "you" | "org";
export type SettingsKey = "profile" | "preferences" | "notifications" | "general" | "channels" | "team" | "ai" | "plan" | "api" | "data";

export type SettingsSection = { key: SettingsKey; group: SettingsGroup; to: `/settings/${string}`; icon: LucideIcon };

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  { key: "profile", group: "you", to: "/settings/profile", icon: UserRound },
  { key: "preferences", group: "you", to: "/settings/preferences", icon: SlidersHorizontal },
  { key: "notifications", group: "you", to: "/settings/notifications", icon: Bell },
  { key: "general", group: "org", to: "/settings/general", icon: Building2 },
  { key: "channels", group: "org", to: "/settings/channels", icon: Radio },
  { key: "team", group: "org", to: "/settings/team", icon: Users },
  { key: "ai", group: "org", to: "/settings/ai", icon: Sparkles },
  { key: "plan", group: "org", to: "/settings/plan", icon: Gauge },
  { key: "api", group: "org", to: "/settings/api", icon: Code2 },
  { key: "data", group: "org", to: "/settings/data", icon: DatabaseZap },
];

export const SETTINGS_GROUPS: readonly SettingsGroup[] = ["you", "org"];

/**
 * Old settings URLs → where that content lives now (kept forever: notification links, bookmarks and e-mails point at
 * them). The hash lands on the moved section.
 */
export const SETTINGS_REDIRECTS = {
  "/settings/appearance": { to: "/settings/preferences" },
  "/settings/billing": { to: "/settings/plan", hash: "billing" },
  "/settings/tags": { to: "/settings/ai", hash: "tags" },
  "/settings/calendar": { to: "/settings/general", hash: "calendar" },
} as const;

export const sectionFor = (pathname: string): SettingsSection | undefined => SETTINGS_SECTIONS.find((s) => pathname === s.to || pathname.startsWith(`${s.to}/`));
